import { ConfigurableToolInputSchemas } from "@app/lib/actions/mcp_internal_actions/input_schemas";
import {
  POD_TASK_STATUSES,
  TASK_CONFIDENCES,
  TASK_EVIDENCE_SOURCE_KINDS,
} from "@app/types/project_task";
import { INTERNAL_MIME_TYPES } from "@memoair/client";
import { z } from "zod";

const PodTasksDustPodInputSchema =
  ConfigurableToolInputSchemas[
    INTERNAL_MIME_TYPES.TOOL_INPUT.DUST_POD
  ].optional();

const PodTasksCreateTaskSourceInputSchema = z.object({
  url: z
    .string()
    .url()
    .describe(
      "URL of a related source (e.g. Slack thread, GitHub issue, Notion page)"
    ),
  title: z.string().describe("Human-readable title for the source"),
});

const PodTasksDueOnInputSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD")
  .describe("Due date, YYYY-MM-DD.");

const PodTasksEvidenceInputSchema = z.object({
  source: z
    .object({
      kind: z
        .enum(TASK_EVIDENCE_SOURCE_KINDS)
        .describe(
          "What the quote comes from: a meeting, a file (such as a transcript in this Pod), a conversation, an email thread or a Slack thread."
        ),
      id: z
        .string()
        .min(1)
        .max(255)
        .describe(
          "The source's identifier. For a file, its path exactly as the files tools show it (for example conversation-<id>/<file name>.md or pod-<id>/<file name>.md), or its file id. For a conversation, its sId; for a meeting or thread, its id."
        ),
      title: z.string().optional().describe("Human-readable source title."),
      url: z.string().url().optional().describe("Link to the source."),
    })
    .describe("Where the quote comes from."),
  excerpt: z
    .string()
    .min(1)
    .describe(
      "The span copied verbatim from the source, character for character. Use '...' to skip text; every remaining piece must be verbatim. It is checked against the source; a quote not found is kept and marked unverified."
    ),
  locator: z
    .object({
      kind: z.enum(["timestamp", "message"]),
      value: z
        .string()
        .min(1)
        .describe("A timestamp such as 00:14:32, or a message sId."),
    })
    .optional()
    .describe("Where in the source the quote sits."),
});

const PodTasksProposedOwnerInputSchema = z
  .union([
    z.literal("unclear"),
    z.object({
      name: z
        .string()
        .optional()
        .describe(
          "The owner's full name as the source or attendee list gives it."
        ),
      email: z
        .string()
        .max(255)
        .optional()
        .describe("The owner's email, when known."),
    }),
  ])
  .describe(
    "Who should own the task, as a person, or 'unclear' when nobody can tell. It is resolved to a Pod member, a workspace member or an outside contact; never write it into the text."
  );

const PodTasksMeetingInputSchema = z
  .object({
    title: z
      .string()
      .max(255)
      .optional()
      .describe(
        "The meeting's title as the transcript or invite gives it, without the date."
      ),
    startedAt: z
      .string()
      .optional()
      .describe("When the meeting started, ISO 8601, when the source says."),
    attendees: z
      .array(
        z.object({
          name: z.string().optional(),
          email: z.string().max(255).optional(),
        })
      )
      .max(100)
      .optional()
      .describe("Who attended, as the transcript or invite lists them."),
    owners: z
      .array(PodTasksProposedOwnerInputSchema)
      .max(10)
      .optional()
      .describe(
        "Who runs this meeting, such as its organizer or chair, when the source says. A person confirms them before they see the meeting's tasks."
      ),
  })
  .optional()
  .describe(
    "The meeting the transcript records, when the evidence quotes one. Every task is linked to it."
  );

const PodTasksTaskFieldsSchema = {
  text: z
    .string()
    .min(16)
    .max(256)
    .describe(
      "What has to be done. Keep the owner and the deadline out of it: " +
        "set them with proposedOwner (or userId) and dueOn."
    ),
  proposedOwner: PodTasksProposedOwnerInputSchema.optional(),
  dueOn: PodTasksDueOnInputSchema.optional().describe(
    "Due date, YYYY-MM-DD. Set it whenever a deadline was stated; resolve relative dates ('Friday') against today."
  ),
  confidence: z
    .enum(TASK_CONFIDENCES)
    .optional()
    .describe(
      "How sure you are of the task and its owner: high when named, medium when inferred, low for a weak inference or an unclear owner."
    ),
  note: z
    .string()
    .max(1000)
    .optional()
    .describe(
      "A short note a reviewer should see, such as your reading of a likely transcription error."
    ),
  rationale: z
    .string()
    .optional()
    .describe(
      "Why this task exists, in your words. The verbatim evidence goes in evidence, not here."
    ),
  evidence: z
    .array(PodTasksEvidenceInputSchema)
    .max(5)
    .optional()
    .describe(
      "The verbatim quotes this task comes from. A quote that is not found in a source the server can read is kept and marked unverified."
    ),
};

const PodTasksCreateTaskInputSchema = z.object({
  ...PodTasksTaskFieldsSchema,
  userId: z
    .union([z.string(), z.null()])
    .optional()
    .describe(
      "User sId of a Pod member to assign directly, when the person asking named them. Otherwise set proposedOwner; never default to yourself or the requester."
    ),
  doneRationale: z
    .string()
    .optional()
    .describe(
      "The rationale for marking the task as done. Leave empty if the task should not be marked as done."
    ),
  sources: z
    .array(PodTasksCreateTaskSourceInputSchema)
    .optional()
    .describe(
      "Optional context sources to attach to this task when the agent can provide them"
    ),
});

const PodTasksAnsweredQuestionIdSchema = z
  .string()
  .describe(
    "The action id of the ask_user_question card the person answered to choose these tasks, when you know it. Leave it out to use your latest card the person answered in this conversation; their answer is what lets the tasks be created without another prompt."
  );

export const PodTasksCreateTasksInputSchema = z.object({
  creatorType: z
    .enum(["user", "agent"])
    .optional()
    .describe(
      "Deprecated and ignored: who asked is read from the conversation."
    ),
  tasks: z
    .array(PodTasksCreateTaskInputSchema)
    .min(1)
    .max(30)
    .describe("List of tasks to create (max 30)."),
  meeting: PodTasksMeetingInputSchema,
  dustPod: PodTasksDustPodInputSchema.describe(
    "Optional Pod to create the tasks in; falls back to the conversation's Pod."
  ),
});

export const PodTasksProposeTasksInputSchema = z.object({
  tasks: z
    .array(z.object(PodTasksTaskFieldsSchema))
    .min(1)
    .max(30)
    .describe("Tasks to propose for a person to review (max 30)."),
  meeting: PodTasksMeetingInputSchema,
  dustPod: PodTasksDustPodInputSchema.describe(
    "Optional Pod to propose the tasks in; falls back to the conversation's Pod."
  ),
});

export const PodTasksAcceptProposalsInputSchema = z.object({
  answeredQuestionId: PodTasksAnsweredQuestionIdSchema.optional(),
  accept: z
    .array(
      z.object({
        taskId: z.string().describe("The sId of the proposed task."),
        text: PodTasksTaskFieldsSchema.text.optional(),
        proposedOwner: PodTasksProposedOwnerInputSchema.optional(),
        dueOn: PodTasksDueOnInputSchema.nullable().optional(),
      })
    )
    .max(30)
    .describe(
      "Proposals the person chose, with any changes they asked for in free text."
    ),
  dismiss: z
    .array(z.string())
    .max(30)
    .optional()
    .describe(
      "Proposals the person declined, such as every one when they answered 'Create none'. Leave out anything they did not rule on."
    ),
  dustPod: PodTasksDustPodInputSchema.describe(
    "Optional Pod the proposals are in; falls back to the conversation's Pod."
  ),
});

export type PodTasksMeetingInput = z.infer<typeof PodTasksMeetingInputSchema>;
export type PodTasksEvidenceInput = z.infer<typeof PodTasksEvidenceInputSchema>;
export type PodTasksProposedOwnerInput = z.infer<
  typeof PodTasksProposedOwnerInputSchema
>;

export type PodTasksCreateTasksInput = z.infer<
  typeof PodTasksCreateTasksInputSchema
>;

export function isPodTasksCreateTasksInput(
  input: Record<string, unknown>
): input is PodTasksCreateTasksInput {
  return PodTasksCreateTasksInputSchema.safeParse(input).success;
}

export const PodTasksUpdateTaskItemInputSchema = z.object({
  taskId: z.string().describe("The sId of the task to update."),
  text: z
    .string()
    .min(16)
    .max(256)
    .optional()
    .describe(
      "The new task description. Keep the owner and the deadline out of it: set them with assigneeUserId and dueOn."
    ),
  assigneeUserId: z
    .union([z.string(), z.null()])
    .optional()
    .describe(
      "The sId of the user to assign the task to (the task's new assignee); must be a member of the Pod. Pass null to unassign. Omit to keep the current assignee."
    ),
  dueOn: PodTasksDueOnInputSchema.nullable()
    .optional()
    .describe(
      "New due date, YYYY-MM-DD. Pass null to clear it. Omit to keep the current one."
    ),
  doneRationale: z
    .string()
    .optional()
    .describe(
      "The rationale for marking the task as done. Leave empty if the task should not be marked as done."
    ),
  status: z
    .enum(POD_TASK_STATUSES)
    .optional()
    .describe(
      "The new task status. 'in_review' means the work is finished and waiting on a human review. Defaults to the current status."
    ),
  markAsDoneByType: z
    .enum(["user", "agent"])
    .optional()
    .describe(
      "Who should be recorded as having marked this task done. Use 'user' when the user explicitly asked for it; 'agent' when the agent is autonomously closing it. Only consulted when the task transitions to 'done'. Defaults to 'agent' if omitted."
    ),
});

export const PodTasksUpdateTasksInputSchema = z.object({
  tasks: z
    .array(PodTasksUpdateTaskItemInputSchema)
    .min(1)
    .max(30)
    .describe("List of tasks to update (max 30)."),
  dustPod: PodTasksDustPodInputSchema.describe(
    "Optional Pod to look up the tasks in; falls back to the conversation's Pod."
  ),
});

export type PodTasksUpdateTasksInput = z.infer<
  typeof PodTasksUpdateTasksInputSchema
>;

export type PodTasksUpdateTaskItemInput = z.infer<
  typeof PodTasksUpdateTaskItemInputSchema
>;

export function isPodTasksUpdateTasksInput(
  input: Record<string, unknown>
): input is PodTasksUpdateTasksInput {
  return PodTasksUpdateTasksInputSchema.safeParse(input).success;
}
